import { useBreakpoint } from "@avenue-8/sidekick-ui";
import { useCallback, useEffect, useState } from "react";

type Props = {
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  scrollOnLoad: boolean;
  numberOfMessages: number;
  currentThread: string | undefined;
  isLoadingMessages: boolean;
};

export const useScrollToLastMessage = (props: Props) => {
  const {
    messagesContainerRef,
    scrollOnLoad,
    numberOfMessages,
    currentThread,
    isLoadingMessages,
  } = props;
  const [scrolledThreadId, setScrolledThreadId] = useState<string | null>(null);
  const isLaptopDown = useBreakpoint("laptop");

  const handleScrollToLastMessage = useCallback(
    (opts?: { behavior?: "smooth" | "instant"; extraOffset?: number }) => {
      const behavior = opts?.behavior || "smooth";

      if (!messagesContainerRef.current) return;

      const container = messagesContainerRef.current;
      const child = container.lastElementChild as HTMLDivElement;
      const lastMessageDiv = child?.lastElementChild as HTMLDivElement;
      const offset = 320 + (opts?.extraOffset || 0);

      if (lastMessageDiv) {
        const scrollPosition =
          lastMessageDiv.offsetTop - offset - container.offsetTop;
        container.scrollTo({
          top: scrollPosition,
          behavior,
        });
      }
    },
    [messagesContainerRef],
  );

  useEffect(() => {
    if (
      !scrollOnLoad ||
      numberOfMessages === 0 ||
      isLoadingMessages ||
      scrolledThreadId === currentThread
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      handleScrollToLastMessage({
        behavior: "instant",
        extraOffset: isLaptopDown ? 0 : 300,
      });
      setScrolledThreadId(currentThread || null);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    currentThread,
    scrolledThreadId,
    scrollOnLoad,
    numberOfMessages,
    isLoadingMessages,
    isLaptopDown,
    handleScrollToLastMessage,
  ]);

  return {
    handleScrollToLastMessage,
    scrolledThreadId,
  };
};
